import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useForm } from "react-hook-form";
import { promptTemplateApi } from "@/apis/promptTemplate";
import Router from "@/constants/router";
import { PromptTemplateDto, UpdatePromptTemplateDto } from "@/dto/PromptTemplateDto";

interface FormData extends UpdatePromptTemplateDto {}

/**
 * 보스베이비 프롬프트 템플릿 상세 커스텀 훅
 *
 * @description 보스베이비 채팅 프롬프트 템플릿 상세/수정 비즈니스 로직을 관리하는 훅
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-29
 */
export default function useBossBabyPromptTemplateDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PromptTemplateDto | null>(null);

  const form = useForm<FormData>({
    defaultValues: {
      templateKey: "boss_baby_chat", // 🔥 보스베이비 채팅 프롬프트 키로 고정
      templateName: "",
      templateDescription: "",
      templateCategory: "boss_baby", // 🔥 보스베이비 카테고리로 고정
      systemPrompt: "",
      userPromptTemplate: "",
      assistantPrompt: "",
      promptVariables: "",
      aiModelProvider: "OPENAI",
      aiModelName: "gpt-4o",
      aiModelTemperature: 0.7,
      aiMaxTokens: 4000,
      aiModelConfig: "",
      useCondition: "",
      priorityOrder: 1,
      isActive: true,
    },
  });

  const fetchData = useCallback(async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const result = await promptTemplateApi.getDetail(Number(id));
      
      // 🔥 보스베이비 채팅 프롬프트가 아닌 경우 리다이렉트
      if (result.templateKey !== 'boss_baby_chat') {
        enqueueSnackbar("This is not a Boss Baby prompt template.", { variant: "error" });
        navigate(Router.BossBabyPromptTemplate);
        return;
      }
      
      setData(result);
      
      form.reset({
        templateKey: result.templateKey,
        templateName: result.templateName,
        templateDescription: result.templateDescription || "",
        templateCategory: result.templateCategory,
        systemPrompt: result.systemPrompt || "",
        userPromptTemplate: result.userPromptTemplate || "",
        assistantPrompt: result.assistantPrompt || "",
        promptVariables: result.promptVariables ? JSON.stringify(result.promptVariables, null, 2) : "",
        aiModelProvider: result.aiModelProvider,
        aiModelName: result.aiModelName,
        aiModelTemperature: result.aiModelTemperature,
        aiMaxTokens: result.aiMaxTokens,
        aiModelConfig: result.aiModelConfig ? JSON.stringify(result.aiModelConfig, null, 2) : "",
        useCondition: result.useCondition || "",
        priorityOrder: result.priorityOrder,
        isActive: result.isActive,
      });
    } catch (error: any) {
      enqueueSnackbar(error.message || "Failed to fetch prompt template", { variant: "error" });
      navigate(Router.BossBabyPromptTemplate);
    } finally {
      setLoading(false);
    }
  }, [id, form, navigate, enqueueSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = useCallback(async (formData: FormData) => {
    if (!id) return;
    
    setLoading(true);
    try {
      const updateData: UpdatePromptTemplateDto = {
        ...formData,
        templateKey: "boss_baby_chat", // 🔥 강제로 boss_baby_chat으로 설정
        templateCategory: "boss_baby", // 🔥 강제로 boss_baby로 설정
        promptVariables: formData.promptVariables ? JSON.parse(formData.promptVariables) : null,
        aiModelConfig: formData.aiModelConfig ? JSON.parse(formData.aiModelConfig) : null,
      };

      await promptTemplateApi.update(Number(id), updateData);
      enqueueSnackbar("Boss Baby prompt template updated successfully.", { variant: "success" });
      navigate(Router.BossBabyPromptTemplate);
    } catch (error: any) {
      enqueueSnackbar(error.message || "Failed to update prompt template", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [id, navigate, enqueueSnackbar]);

  const handleDelete = useCallback(async () => {
    if (!id) return;
    if (!confirm("Are you sure you want to delete this Boss Baby prompt template?")) return;
    
    setLoading(true);
    try {
      await promptTemplateApi.delete(Number(id));
      enqueueSnackbar("Boss Baby prompt template deleted successfully.", { variant: "success" });
      navigate(Router.BossBabyPromptTemplate);
    } catch (error: any) {
      enqueueSnackbar(error.message || "Failed to delete prompt template", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [id, navigate, enqueueSnackbar]);

  const handleCancel = useCallback(() => {
    navigate(Router.BossBabyPromptTemplate);
  }, [navigate]);

  return {
    data,
    form,
    loading,
    handleSubmit,
    handleDelete,
    handleCancel,
  };
}









